import { useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router'
import { errorMessage, post } from '../api'
import { useAuth } from '../auth'
import { Alert, Button, Field, Input } from '../components/ui'
import { encodeQr } from '../qr'

/** Entering the authenticator code on a new session, or setting an authenticator up. */
export function MfaPage() {
  const { me, refresh, signOut } = useAuth()
  const [params] = useSearchParams()
  const returnTo = params.get('returnTo') ?? '/'
  if (!me) return null
  if (!me.mfaRequired && !me.mfaSetupRequired) return <Navigate to={returnTo} replace />
  return (
    <div className="auth-page">
      <div className="auth-card">
        <Link to="/" className="brand">
          <span className="brand-mark" /> Tabula
        </Link>
        {me.mfaRequired ? <VerifyForm onDone={() => refresh()} /> : <SetupForm onDone={() => refresh()} />}
        <p className="small muted" style={{ marginTop: 18, textAlign: 'center' }}>
          Signed in as {me.user.email}.{' '}
          <button className="link-btn" onClick={() => void signOut()}>
            Sign out
          </button>
        </p>
      </div>
    </div>
  )
}

function VerifyForm({ onDone }: { onDone: () => Promise<unknown> }) {
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await post('/v1/auth/totp/verify', { code })
      await onDone()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }
  return (
    <form onSubmit={submit} className="stack">
      <h1>Enter your code</h1>
      <p className="muted small">Open your authenticator app and enter the 6-digit code for Tabula.</p>
      {error ? <Alert>{error}</Alert> : null}
      <Field label="Code">{(id) => <Input id={id} inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]*" value={code} onChange={(e) => setCode(e.target.value)} autoFocus required />}</Field>
      <Button type="submit" variant="primary" size="lg" busy={busy}>
        Continue
      </Button>
    </form>
  )
}

export function SetupForm({ onDone, intro }: { onDone: () => Promise<unknown>; intro?: string }) {
  const navigate = useNavigate()
  const [setup, setSetup] = useState<{ secret: string; uri: string } | null>(null)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const begin = async () => {
    setBusy(true)
    setError(null)
    try {
      setSetup(await post<{ secret: string; uri: string }>('/v1/auth/totp/setup'))
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }
  const confirm = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await post('/v1/auth/totp/confirm', { code })
      await onDone()
      navigate('/', { replace: true })
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  if (!setup) {
    return (
      <div className="stack">
        <h1>Set up an authenticator</h1>
        <p className="muted small">{intro ?? 'Platform admin access needs a second factor. You will need an authenticator app such as Google Authenticator, Microsoft Authenticator, 1Password or Authy.'}</p>
        {error ? <Alert>{error}</Alert> : null}
        <Button variant="primary" size="lg" busy={busy} onClick={() => void begin()}>
          Start setup
        </Button>
      </div>
    )
  }
  return (
    <form onSubmit={confirm} className="stack">
      <h1>Add Tabula to your app</h1>
      <p className="muted small">Open your authenticator app, choose "add account" and scan the code, or enter the key by hand. Then type the 6-digit code it shows to confirm.</p>
      <div style={{ display: 'grid', placeItems: 'center' }}>
        <QrCode value={setup.uri} />
      </div>
      <p className="small">
        Key: <code className="mono">{setup.secret.match(/.{1,4}/g)?.join(' ')}</code>
      </p>
      {error ? <Alert>{error}</Alert> : null}
      <Field label="Code from the app">{(id) => <Input id={id} inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} required />}</Field>
      <Button type="submit" variant="primary" size="lg" busy={busy}>
        Confirm and enable
      </Button>
    </form>
  )
}

/** A QR code drawn as SVG (see qr.ts for the encoder). */
function QrCode({ value }: { value: string }) {
  const modules = encodeQr(value)
  if (!modules) return <p className="small muted">The key is too long for a code here; enter it by hand.</p>
  const n = modules.length
  const size = 196
  const cell = size / (n + 8)
  let path = ''
  for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) if (modules[y]![x]) path += `M${(x + 4) * cell} ${(y + 4) * cell}h${cell}v${cell}h-${cell}z`
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="QR code for your authenticator app" style={{ background: '#fff', borderRadius: 8 }}>
      <path d={path} fill="#000" />
    </svg>
  )
}
